/**
 * Dev-only instrumentation.
 *
 * Exists because two rounds of reasoning about this gallery's scroll performance produced two
 * wrong answers. Counters and long-task durations beat intuition.
 *
 * Everything here is behind `import.meta.env.DEV` at the call sites and is tree-shaken from
 * production builds.
 */

export interface Counters {
  tileRenders: number;
  ioPreload: number;
  ioRetain: number;
  resolveStarted: number;
  resolveDone: number;
  released: number;
}

const counters: Counters = {
  tileRenders: 0,
  ioPreload: 0,
  ioRetain: 0,
  resolveStarted: 0,
  resolveDone: 0,
  released: 0,
};

export function bump(key: keyof Counters, by = 1): void {
  counters[key] += by;
}

/** Time spent inside scroll subscribers, accumulated between samples. */
let scrollCallbackMs = 0;
export function addScrollCallbackMs(ms: number): void {
  scrollCallbackMs += ms;
}

// --- long tasks -------------------------------------------------------------

interface LongTask {
  duration: number;
  at: number;
}

let longTasks: LongTask[] = [];
let observerStarted = false;

function startLongTaskObserver() {
  if (observerStarted || typeof PerformanceObserver === 'undefined') return;
  observerStarted = true;
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        longTasks.push({ duration: Math.round(entry.duration), at: Math.round(entry.startTime) });
      }
    }).observe({ entryTypes: ['longtask'] });
  } catch {
    // Not supported (Safari). fps and the counters still work.
  }
}

// --- sampling ---------------------------------------------------------------

export interface Sample {
  t: number;
  fps: number;
  worstFrameMs: number;
  scrollCbMs: number;
  longTasks: number;
  longestTaskMs: number;
  delta: Counters;
  queue: { active: number; queued: number };
  tiles: number;
}

const STORAGE_KEY = 'gallery-perf-session';
const MAX_SAMPLES = 240; // ~2 minutes at 500ms windows

let samples: Sample[] = [];
let previous: Counters = { ...counters };

export function recordSample(input: {
  fps: number;
  worstFrameMs: number;
  queue: { active: number; queued: number };
  tiles: number;
}): Sample {
  startLongTaskObserver();

  const delta: Counters = {
    tileRenders: counters.tileRenders - previous.tileRenders,
    ioPreload: counters.ioPreload - previous.ioPreload,
    ioRetain: counters.ioRetain - previous.ioRetain,
    resolveStarted: counters.resolveStarted - previous.resolveStarted,
    resolveDone: counters.resolveDone - previous.resolveDone,
    released: counters.released - previous.released,
  };
  previous = { ...counters };

  const longestTaskMs = longTasks.reduce((max, task) => Math.max(max, task.duration), 0);

  const sample: Sample = {
    t: Math.round(performance.now()),
    fps: input.fps,
    worstFrameMs: input.worstFrameMs,
    scrollCbMs: Math.round(scrollCallbackMs * 100) / 100,
    longTasks: longTasks.length,
    longestTaskMs,
    delta,
    queue: input.queue,
    tiles: input.tiles,
  };

  scrollCallbackMs = 0;
  longTasks = [];

  samples.push(sample);
  if (samples.length > MAX_SAMPLES) samples.shift();

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(samples));
  } catch {
    // Quota or private mode — the in-memory copy still exports.
  }

  return sample;
}

export function resetSession(): void {
  samples = [];
  longTasks = [];
  scrollCallbackMs = 0;
  previous = { ...counters };
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * A digest built for pasting into a conversation: the worst frames first, since those are the
 * ones that get felt, plus totals to show where the work went.
 */
export function exportReport(): string {
  const active = samples.filter((s) => s.fps > 0);
  if (active.length === 0) return 'No samples recorded yet — scroll first.';

  const worst = [...active].sort((a, b) => b.worstFrameMs - a.worstFrameMs).slice(0, 8);
  const totals = active.reduce(
    (acc, s) => ({
      tileRenders: acc.tileRenders + s.delta.tileRenders,
      ioPreload: acc.ioPreload + s.delta.ioPreload,
      ioRetain: acc.ioRetain + s.delta.ioRetain,
      resolveStarted: acc.resolveStarted + s.delta.resolveStarted,
      resolveDone: acc.resolveDone + s.delta.resolveDone,
      released: acc.released + s.delta.released,
      scrollCbMs: acc.scrollCbMs + s.scrollCbMs,
      longTasks: acc.longTasks + s.longTasks,
    }),
    {
      tileRenders: 0,
      ioPreload: 0,
      ioRetain: 0,
      resolveStarted: 0,
      resolveDone: 0,
      released: 0,
      scrollCbMs: 0,
      longTasks: 0,
    }
  );

  return JSON.stringify(
    {
      tiles: active[active.length - 1].tiles,
      windows: active.length,
      fps: {
        min: Math.min(...active.map((s) => s.fps)),
        median: active.map((s) => s.fps).sort((a, b) => a - b)[Math.floor(active.length / 2)],
        max: Math.max(...active.map((s) => s.fps)),
      },
      worstFrameMs: Math.max(...active.map((s) => s.worstFrameMs)),
      longestTaskMs: Math.max(...active.map((s) => s.longestTaskMs)),
      totals: { ...totals, scrollCbMs: Math.round(totals.scrollCbMs) },
      worstWindows: worst.map((s) => ({
        fps: s.fps,
        worstFrameMs: s.worstFrameMs,
        longestTaskMs: s.longestTaskMs,
        scrollCbMs: s.scrollCbMs,
        renders: s.delta.tileRenders,
        io: s.delta.ioPreload + s.delta.ioRetain,
        resolves: s.delta.resolveStarted,
        releases: s.delta.released,
        queue: s.queue,
      })),
    },
    null,
    2
  );
}
